import api from './api';

/**
 * Serviço para gerenciar preferências do usuário
 */
class UserPreferencesService {
  /**
   * Obter preferências do usuário
   * @returns {Promise<Object|null>} Preferências do usuário
   */
  async getPreferences() {
    try {
      const response = await api.get('/preferencias');
      if (response.data?.status === 'sucesso') {
        const prefs = response.data.dados;
        // ✅ Salvar no localStorage para sincronização com ThemeProvider
        if (prefs) {
          this.savePreferencesToLocalStorage(prefs);
          // ✅ Também salvar o tema separadamente para o ThemeProvider
          if (prefs.theme_mode) {
            localStorage.setItem('themeMode', prefs.theme_mode);
          }
        }
        return prefs;
      }
      console.warn('⚠️ API retornou status diferente de sucesso');
      return null;
    } catch (error) {
      console.error('❌ Erro ao obter preferências da API:', error);
      console.error('   Detalhes:', error.response?.data || error.message);
      // Fallback para localStorage
      return this.getPreferencesFromLocalStorage();
    }
  }

  /**
   * Salvar/Atualizar preferências do usuário
   * @param {Object} preferences - Preferências a serem salvas
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async savePreferences(preferences) {
    try {
      const response = await api.put('/preferencias', preferences);
      if (response.data?.status === 'sucesso') {
        // Salvar também no localStorage como backup
        this.savePreferencesToLocalStorage(preferences);
        return true;
      }
      console.warn('⚠️ API retornou status diferente de sucesso:', response.data);
      return false;
    } catch (error) {
      console.error('❌ Erro ao salvar preferências na API:', error);
      console.error('   Detalhes:', error.response?.data || error.message);
      // Fallback para localStorage
      return this.savePreferencesToLocalStorage(preferences);
    }
  }

  /**
   * Atualizar apenas o tema
   * @param {string} themeMode - Modo do tema
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async updateTheme(themeMode) {
    try {
      const response = await api.put('/preferencias', { theme_mode: themeMode });
      if (response.data?.status === 'sucesso') {
        this.updateThemeInLocalStorage(themeMode);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao atualizar tema:', error);
      return this.updateThemeInLocalStorage(themeMode);
    }
  }

  /**
   * Atualizar tamanho da fonte
   * @param {string} fontSize - Tamanho da fonte
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async updateFontSize(fontSize) {
    try {
      const response = await api.put('/preferencias', { font_size: fontSize });
      if (response.data?.status === 'sucesso') {
        this.updateFontSizeInLocalStorage(fontSize);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao atualizar tamanho da fonte:', error);
      return this.updateFontSizeInLocalStorage(fontSize);
    }
  }

  /**
   * Salvar foto do usuário
   * @param {string} photoBase64 - Foto em base64
   * @param {Object} metadata - Metadados da foto
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async saveUserPhoto(photoBase64, metadata = {}) {
    try {
      const response = await api.put('/preferencias', {
        user_photo: photoBase64,
        photo_metadata: metadata
      });
      if (response.data?.status === 'sucesso') {
        this.saveUserPhotoToLocalStorage(photoBase64, metadata);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao salvar foto:', error);
      return this.saveUserPhotoToLocalStorage(photoBase64, metadata);
    }
  }

  /**
   * Remover foto do usuário
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async removeUserPhoto() {
    try {
      const response = await api.put('/preferencias', {
        user_photo: null,
        photo_metadata: null
      });
      if (response.data?.status === 'sucesso') {
        this.removeUserPhotoFromLocalStorage();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao remover foto:', error);
      return this.removeUserPhotoFromLocalStorage();
    }
  }

  // ========== Métodos de localStorage (fallback) ==========

  getPreferencesFromLocalStorage() {
    try {
      const stored = localStorage.getItem('user_preferences');
      if (stored) {
        return JSON.parse(stored);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  savePreferencesToLocalStorage(preferences) {
    try {
      const dataToSave = {
        ...preferences,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      localStorage.setItem('user_preferences', JSON.stringify(dataToSave));
      return true;
    } catch (error) {
      return false;
    }
  }

  updateThemeInLocalStorage(themeMode) {
    try {
      const existing = this.getPreferencesFromLocalStorage() || {};
      const updated = {
        ...existing,
        theme_mode: themeMode,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      localStorage.setItem('user_preferences', JSON.stringify(updated));
      return true;
    } catch (error) {
      return false;
    }
  }

  updateFontSizeInLocalStorage(fontSize) {
    try {
      const existing = this.getPreferencesFromLocalStorage() || {};
      const updated = {
        ...existing,
        font_size: fontSize,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      localStorage.setItem('user_preferences', JSON.stringify(updated));
      return true;
    } catch (error) {
      return false;
    }
  }

  saveUserPhotoToLocalStorage(photoBase64, metadata = {}) {
    try {
      const existing = this.getPreferencesFromLocalStorage() || {};
      const updated = {
        ...existing,
        user_photo: photoBase64,
        photo_metadata: metadata,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      localStorage.setItem('user_preferences', JSON.stringify(updated));
      return true;
    } catch (error) {
      return false;
    }
  }

  removeUserPhotoFromLocalStorage() {
    try {
      const existing = this.getPreferencesFromLocalStorage() || {};
      const updated = {
        ...existing,
        user_photo: null,
        photo_metadata: null,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      localStorage.setItem('user_preferences', JSON.stringify(updated));
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Instância singleton do serviço
const userPreferencesService = new UserPreferencesService();

export default userPreferencesService;

